<template>
  <div class="following-page">
    <div class="main">
      <div class="top">
        <h2 class="title">{{ pageTitle }}</h2>
        <ul class="instructions" v-if="showInstructions">
          <li>
            <font-awesome-icon icon="check-circle" class="follow" />
            <span><b>Follow</b> artists to see their items on your feed.</span>
          </li>
          <li>
            <img src="../assets/bell.png" alt="" class="bell" />
            <span
              >Turn on the bell to receive <b>real-time email alerts</b> for
              shop drops.</span
            >
          </li>
        </ul>
        <div class="subtitle-row">
          <h6 class="label">{{ pageDescription }}</h6>
          <h6 class="select-all" @click="toggleFollowAll">
            {{ followAllText }}
          </h6>
        </div>
      </div>

      <div class="scroll-container">
        <div class="shop-listings" :class="{ loading: loading }">
          <loader :loading="loading" :large="true" />
          <div v-if="error" class="error">Error</div>
          <div v-if="shops" class="content">
            <shop-listing
              v-for="shop in shops"
              :key="shop.id"
              :shop="shop"
              :canClickCell="false"
              @onFollow="shopFollowed(shop)"
              @onUnfollow="shopUnfollowed(shop)"
              @onMute="shopMuted(shop)"
              @onUnmute="shopUnmuted(shop)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ShopService from "@/services/ShopService";
import ShopListing from "@/components/ShopListing.vue";
import { mapGetters } from "vuex";
import { library } from "@fortawesome/fontawesome-svg-core";
import Loader from "@/components/Loader.vue";
export default {
  name: "OnboardPage",
  components: {
    ShopListing,
    Loader,
  },
  props: {
    pageType: {
      type: String,
      default: "spotify",
    },
    showInstructions: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      loading: false,
      error: false,
      shops: [],
      isFollowingAll: false,
    };
  },
  computed: {
    ...mapGetters({
      getFollowedShops: "shop/getFollowedShops",
      didFetchSpotifyShops: "onboard/didFetchSpotifyShops",
      didFetchPopularShops: "onboard/didFetchPopularShops",
      getSpotifyShops: "onboard/getSpotifyShops",
      getPopularShops: "onboard/getPopularShops",
    }),
    isSpotifyPage() {
      return this.pageType === "spotify";
    },
    isPopularPage() {
      return this.pageType === "popular";
    },
    pageTitle() {
      if (this.isSpotifyPage) {
        return "Follow your favorite artists";
      } else {
        return "Follow popular artists";
      }
    },
    pageDescription() {
      if (this.isSpotifyPage) {
        return "Your Top Spotify Artists";
      } else {
        return "Popular Artists";
      }
    },
    followAllText() {
      if (this.isFollowingAll) {
        return "Unfollow All";
      } else {
        return "Follow All";
      }
    },
  },
  created() {
    this.reset();
    if (this.isPopularPage) {
      this.isFollowingAll = false;
    }
  },
  watch: {
    pageType(value, oldValue) {
      this.reset();
    },
  },
  methods: {
    reset() {
      this.shops = [];
      this.fetchData();
      this.isFollowingAll = false;
    },
    toggleFollowAll() {
      if (this.isFollowingAll) {
        this.$store.dispatch("shop/localUnfollowShops", this.shops);
        this.trackEvent("Onboard", "Toggle All", "Unfollow");
      } else {
        this.$store.dispatch("shop/localFollowShops", this.shops);
        this.trackEvent("Onboard", "Toggle All", "Follow");
      }
      this.isFollowingAll = !this.isFollowingAll;
    },
    fetchData() {
      if (this.isSpotifyPage) {
        if (!this.didFetchSpotifyShops && this.getSpotifyShops.length === 0) {
          this.loading = true;
          ShopService.getSpotifyShops().then(
            (response) => {
              this.loading = false;
              const shops = response.data;
              this.shops = shops;
              this.$store.dispatch("onboard/updateSpotifyShops", shops);
              this.$store.dispatch("onboard/fetchedSpotifyShops");
              this.$store.dispatch("shop/localFollowShops", shops);
              if (!this.shops.length) {
                this.$emit("onEmpty");
              }
            },
            (error) => {
              this.loading = false;
              this.shops = [];
              console.log("There was an error loading spotify shops", error);
              this.$emit("onEmpty");
            }
          );
        } else {
          this.shops = this.getSpotifyShops;
          if (!this.shops.length) {
            this.$emit("onEmpty");
          }
        }
      } else {
        if (!this.didFetchPopularShops && this.getPopularShops.length === 0) {
          const howMany = 50;
          ShopService.getPopularShops({
            limit: howMany,
            offset: 0,
          }).then(
            (response) => {
              this.loading = false;
              this.shops = response.data;
              this.$store.dispatch("onboard/updatePopularShops", this.shops);
              this.$store.dispatch("onboard/fetchedPopularShops");
              if (!this.shops.length) {
                this.$emit("onEmpty");
              }
            },
            (error) => {
              this.loading = false;
              console.log("There was an error loading popular shops", error);
              this.$emit("onEmpty");
            }
          );
        } else {
          this.shops = this.getPopularShops;
          if (!this.shops.length) {
            this.$emit("onEmpty");
          }
        }
      }
    },
    shopFollowed(shop) {
      this.$store.dispatch("shop/localFollowShop", shop);
      this.updateIsFollowingAll();
      this.trackEvent("Onboard", "Follow", shop.name, shop.id);
    },
    shopUnfollowed(shop) {
      this.$store.dispatch("shop/localUnfollowShop", shop);
      this.updateIsFollowingAll();
      this.trackEvent("Onboard", "Unfollow", shop.name, shop.id);
    },
    shopMuted(shop) {
      this.trackEvent("Onboard", "Mute", shop.name, shop.id);
    },
    shopUnmuted(shop) {
      this.trackEvent("Onboard", "Unmute", shop.name, shop.id);
    },
    updateIsFollowingAll() {
      const followedShopIds = this.getFollowedShops.map((s) => s.id);
      if (this.shops.every((s) => followedShopIds.includes(s.id))) {
        this.isFollowingAll = true;
      }
      if (this.shops.every((s) => !followedShopIds.includes(s.id))) {
        this.isFollowingAll = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.top {
  padding: 0 10px;
}
.main {
  height: calc(100vh - 38px - 70px);
  display: flex;
  flex-direction: column;
  @media (min-width: 768px) {
    width: 75%;
    margin: auto;
  }
  @media (min-width: 977px) {
    width: 60%;
    margin: auto;
  }
}
.title {
  margin-top: 10px;
  margin-bottom: 10px;
  font-size: 32px;
  letter-spacing: 0.02em;
  line-height: 1.2;
  vertical-align: middle;
}
.instructions {
  list-style: none;
  padding-left: 4px;
  margin-bottom: 0;
  padding-top: 10px;
  li {
    display: flex;
    align-items: center;
    margin-bottom: 15px;
  }
  svg {
    height: 0.8em;
    min-width: 20px;
  }
  span {
    margin-left: 4px;
    font-size: 12px;
    color: #eee;
    line-height: 1.3;
  }
  .bell {
    width: 18px;
    margin-right: 2px;
  }
}
.subtitle-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 12px;
  margin-top: 15px;
  .label {
    color: #eee;
    font-size: 13px;
    margin-bottom: 0;
  }
  .select-all {
    text-transform: uppercase;
    text-align: center;
    font-size: 11px;
    letter-spacing: 0.02em;
    color: #fff;
    margin-right: 2px;
    margin-bottom: 0;
  }
}

.scroll-container {
  padding: 0 10px;
  flex: 1;
  overflow: auto;
  .shop-listings {
    position: relative;
    max-height: 100%;
    overflow: auto;
    &.loading {
      height: 100%;
    }
  }
}
</style>
